import { ReceiptModal } from "@features/cx/shared/components/ReceiptModal";
import { toast } from "@shared/hooks/use-toast";
import { useCurrency } from "@shared/selectors/globalSelectors";
import React, { useMemo } from "react";
import { usePaymentDetail } from "../../hooks";

const formatLinkedEntity = (linkedData: any): string => {
  if (!linkedData) return "";
  if (typeof linkedData === "object") {
    const identifier =
      linkedData.invoice_no ||
      linkedData.invoiceNo ||
      linkedData.request_no ||
      linkedData.requestNo ||
      linkedData.id ||
      linkedData.referenceNo ||
      linkedData.reference_no;

    return identifier || "";
  }
  return String(linkedData);
};

interface PaymentReceiptProps {
  formData: any;
  isOpen: boolean;
  onClose: () => void;
  paymentId: number;
  autoDownload?: boolean;
  autoPrint?: boolean;
}

export const PaymentReceipt: React.FC<PaymentReceiptProps> = ({
  formData,
  isOpen,
  onClose,
  paymentId,
  autoDownload = false,
  autoPrint = false,
}) => {
  const { data: paymentData } = usePaymentDetail(paymentId);
  const currency = useCurrency();

  // The cached data might have a 'result' wrapper depending on which hook fetched it first
  const actualData = paymentData?.result || paymentData || formData;

  // Transform payment data for receipt
  const receiptData = useMemo(() => {
    if (!actualData) return null;

    const recordedDateTime = actualData.recordedOn?.split(" ") || [];

    return {
      transactionId: actualData.transactionId || "",
      totalAmount: actualData.paymentSummary?.netAmountPaid || "0.00",
      currency,
      consumerName: `${actualData.consumer?.firstName || ""} ${
        actualData.consumer?.lastName || ""
      }`.trim(),
      consumerAccount: actualData.consumer?.consumerNo || "N/A",
      paymentDate: recordedDateTime[0] || "",
      paymentTime: recordedDateTime[1] || "",
      paymentMethod: actualData.paymentMode || "N/A",
      paymentChannel: actualData.paymentChannel || "N/A",
      paymentType: actualData.paymentPayType || "",
      paymentLinkedTo:
        formData?.linkedBillId ||
        formatLinkedEntity(actualData.paymentLinkedTo) ||
        formatLinkedEntity(actualData.linkedEntity) ||
        actualData.linkedTo ||
        actualData.linkedBillId ||
        actualData.paymentDetails?.linkedTo ||
        "N/A",
      breakdown: {
        totalPayableAmount:
          actualData.paymentSummary?.totalPayableAmount || "0.00",
        netAmountPaid: actualData.paymentSummary?.netAmountPaid || "0.00",
        cashReceived: actualData.paymentSummary?.cashReceived || "0.00",
        creditApplied: actualData.paymentSummary?.creditApplied,
        lateFee: actualData.paymentSummary?.lateFee?.amount,
        changeReturned: actualData.paymentSummary?.changeReturned,
      },
      outstandingBalance: actualData.paymentSummary?.outstandingBalance,
    };
  }, [actualData, currency, formData]);

  // Receipt content sections
  const receiptLeftColumn = useMemo(() => {
    if (!receiptData) return [];
    return [
      {
        label: "Consumer",
        value: receiptData.consumerName,
        subValue: receiptData.consumerAccount,
      },
      {
        label: "Payment Method",
        value: `${receiptData.paymentMethod}${
          receiptData.paymentChannel ? ` | ${receiptData.paymentChannel}` : ""
        }`,
      },
      {
        label: formData?.selectedBills?.length > 1 ? `Bills Paid (${formData.selectedBills.length})` : "Payment Linked To",
        value: formData?.selectedBills?.length > 1 ? formData.selectedBills.join(", ") : receiptData.paymentLinkedTo,
        valueClassName: "font-medium text-blue-600 break-words",
      },
    ];
  }, [receiptData, formData]);

  const receiptRightColumn = useMemo(() => {
    if (!receiptData) return [];
    return [
      {
        label: "Payment Date",
        value: receiptData.paymentDate,
        subValue: receiptData.paymentTime,
      },
      {
        label: "Payment Type",
        value: receiptData.paymentType,
      },
    ];
  }, [receiptData]);

  const receiptBreakdownItems = useMemo(() => {
    if (!receiptData) return [];

    const items = [
      {
        label: "Total Payable Amount",
        value: `${receiptData.currency} ${receiptData.breakdown.totalPayableAmount}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      },
      {
        label: "Net Amount Paid",
        value: `${receiptData.currency} ${receiptData.breakdown.netAmountPaid}`,
        className: "font-semibold text-gray-900 pt-3 border-t border-gray-200",
        valueClassName: "font-bold text-blue-600 text-base",
        highlight: true,
      },
      {
        label: "Cash Received",
        value: `${receiptData.currency} ${receiptData.breakdown.cashReceived}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      },
    ];

    if (receiptData.breakdown.creditApplied) {
      items.push({
        label: "Credit Applied",
        value: `${receiptData.currency} ${receiptData.breakdown.creditApplied}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      });
    }

    if (receiptData.breakdown.lateFee) {
      items.push({
        label: "Late Fee",
        value: `${receiptData.currency} ${receiptData.breakdown.lateFee}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      });
    }

    if (receiptData.breakdown.changeReturned) {
      items.push({
        label: "Change Returned",
        value: `${receiptData.currency} ${receiptData.breakdown.changeReturned}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      });
    }

    return items;
  }, [receiptData]);

  if (!receiptData) return null;

  return (
    <ReceiptModal
      isOpen={isOpen}
      onClose={onClose}
      title="Payment Receipt"
      receiptId={receiptData.transactionId}
      navigateToDashboard="/cx/payments"
      maxWidth="max-w-2xl"
      showPrintButton={true}
      showDownloadButton={true}
      autoDownload={autoDownload}
      autoPrint={autoPrint}
      onPrintComplete={() => {
        toast({
          title: "Print Successful",
        });
      }}
      onDownloadComplete={() => {
        toast({
          title: "Download Successful",
        });
      }}
    >
      <div className="space-y-6">
        <div className="text-center py-6 bg-gray-50 rounded-lg border-t-4 border-green-500">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Total Amount Paid
          </p>
          <p className="text-3xl font-bold text-gray-900">
            {receiptData.currency} {receiptData.totalAmount}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <div className="space-y-4">
            {receiptLeftColumn.map((item, index) => (
              <div key={index}>
                <p className="text-xs text-gray-500 tracking-wide mb-1">
                  {item.label}
                </p>
                <p
                  className={
                    item.valueClassName || "font-semibold text-gray-900"
                  }
                >
                  {item.value}
                </p>
                {item.subValue && (
                  <p className="text-sm text-gray-600">{item.subValue}</p>
                )}
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {receiptRightColumn.map((item, index) => (
              <div key={index}>
                <p className="text-xs text-gray-500 tracking-wide mb-1">
                  {item.label}
                </p>
                <p className="font-semibold text-gray-900">{item.value}</p>
                {item.subValue && (
                  <p className="text-sm text-gray-600">{item.subValue}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-5 space-y-3">
          <h3 className="font-semibold text-gray-900 text-sm mb-4">
            Payment Breakdown
          </h3>
          <div className="space-y-3">
            {receiptBreakdownItems.map((item, index) => (
              <div
                key={index}
                className={`flex justify-between items-center text-sm ${item.className}`}
              >
                <span>{item.label}</span>
                <span className={item.valueClassName}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {receiptData.outstandingBalance &&
          Number(receiptData.outstandingBalance) > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 text-sm font-bold">!</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-orange-900 text-sm">
                    Outstanding Balance Remaining
                  </p>
                  <p className="text-sm text-orange-800 mt-1">
                    An outstanding balance of {receiptData.currency}{" "}
                    {receiptData.outstandingBalance} remains for this
                    transaction.
                  </p>
                </div>
              </div>
            </div>
          )}
      </div>
    </ReceiptModal>
  );
};
